/**
 * ModalPopup - A reusable, accessible, and instant popup modal component.
 *
 * Features:
 * - Automatically finds and connects to its trigger element via `aria-controls`.
 * - Closes when clicking the overlay or pressing "Escape".
 * - Appears immediately without animations.
 * - Ensures only one modal is open at a time.
 *
 * Attributes:
 * - `open` (optional) → If present, the modal opens on page load.
 *
 * Accessibility:
 * - Uses `aria-controls` to link the trigger button.
 * - Updates `aria-expanded` on the trigger element when opened/closed.
 * - Adds `aria-modal="true"` and `role="dialog"` for proper accessibility.
 *
 * Notes:
 * - If `.popup-content` is missing, the modal may not function correctly.
 * - If multiple modals exist, opening a new one will close any currently open modals.
 */


class ModalPopup extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.triggerElement = null;
    this.bindEvents();
  }

  bindEvents() {
    this.removeAttribute("hidden");
    this.viewModel = this.getViewModel();

    if (this.viewModel.$closeBtn) {
      this.viewModel.$closeBtn.forEach((cb) => {
        cb.addEventListener('click', () => this.close());
      });
    }

    this.addEventListener("click", (event) => this.handleOutsideClick(event));

    document.addEventListener("keydown", (event) => this.handleKeydown(event));
    this.findTriggerElement();
  }

  getViewModel() {
    return {
      $modal: this.querySelector(".popup-content"),
      $closeBtn: this.querySelectorAll(".close-btn"),
    };
  }

  findTriggerElement() {
    const trigger = document.querySelector(`[aria-controls="${this.id}"]`);
    if (trigger) {
      this.triggerElement = trigger;
      this.triggerElement.addEventListener("click", () => this.open(trigger));
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this.isOpen) {
      this.close();
    }
  }

  handleOutsideClick(event) {
    if (event.target === this) {
      this.close();
    }
  }

  open(trigger) {
    this.triggerElement = trigger;
    if (this.triggerElement) {
      this.triggerElement.setAttribute("aria-expanded", "true");
    }

    this.setAttribute("open", "");
    this.isOpen = true;
    document.body.classList.add("modal-open");

    window.utilities?.trapFocus?.(this);
  }

  close() {
    if (this.triggerElement) {
      this.triggerElement.setAttribute("aria-expanded", "false");
    }

    this.removeAttribute("open");
    this.isOpen = false;
    document.body.classList.remove("modal-open");
  }
}

customElements.define("modal-popup", ModalPopup);